// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AppConfig } from "../../config/config";
import { SnackMessage } from "../../config/constant";
import { APIService } from "../../utils/apiService";
import {
    UpdateUserLeaderboardConsentRequestProps,
    UpdateUserLeaderboardConsentRequestState
} from "../../utils/types";
import { enqueueSnackbarMessage } from "../commonSlice/common";

const initialState: UpdateUserLeaderboardConsentRequestState = {
  state: "idle",
  stateMessage: null,
  errorMessage: null,
  response: null,
  backgroundProcess: false,
  backgroundProcessMessage: null,
};

export const updateLeaderboardConsent = createAsyncThunk(
  "contact/updateLeaderboardConsent",
  async (request: UpdateUserLeaderboardConsentRequestProps, { dispatch, rejectWithValue }) => {
    try {
      const resp = await APIService.getInstance()
        .patch(`${AppConfig.serviceUrls.getContact}/${request.certificateID}/leaderboard`, request.consent);
      
      if (resp.status === 200) {
        dispatch(
          enqueueSnackbarMessage({
            message: SnackMessage.success.leaderboardConsentUpdate,
            type: "success",
          })
        );
        return resp.data;
      } else {
        dispatch(
          enqueueSnackbarMessage({
            message: SnackMessage.error.leaderboardConsentUpdate,
            type: "error",
          })
        );
        return rejectWithValue("Something went wrong");
      }
    } catch (error) {
      dispatch(
        enqueueSnackbarMessage({
          message: SnackMessage.error.leaderboardConsentUpdate,
          type: "error",
        })
      );
      return rejectWithValue(error instanceof Error ? error.message : "Something went wrong");
    }
  }
);

const leaderboardConsentUpdateSlice = createSlice({
  name: "contact/updateLeaderboardConsent",
  initialState,
  reducers: {
    resetLeaderboardConsentState: (state) => {
      state.state = "idle";
      state.stateMessage = null;
      state.errorMessage = null;
      state.backgroundProcess = false;
      state.backgroundProcessMessage = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateLeaderboardConsent.pending, (state) => {
        state.stateMessage = "Updating Leaderboard Consent";
        state.state = "loading";
        state.backgroundProcess = true;
        state.backgroundProcessMessage = "Updating Leaderboard Consent";
        state.errorMessage = null;
      })
      .addCase(updateLeaderboardConsent.fulfilled, (state, action) => {
        state.state = "success";
        state.backgroundProcess = false;
        state.stateMessage = "Leaderboard Consent Updated. It may take a few minutes to reflect the changes.";
        state.response = action.payload;
        state.errorMessage = null;
      })
      .addCase(updateLeaderboardConsent.rejected, (state, action) => {
        state.state = "failed";
        state.backgroundProcess = false;
        state.errorMessage = action.payload as string || "Failed to update leaderboard consent";
        state.stateMessage = null;
      });
  },
});

export const { resetLeaderboardConsentState } = leaderboardConsentUpdateSlice.actions;
export default leaderboardConsentUpdateSlice.reducer;
